import { Injectable } from '@angular/core'
import { ZonedDateTime } from '@js-joda/core'
import { Action, createSelector, Selector, State, StateContext } from '@ngxs/store'
import { patch, ɵPatchSpec } from '@ngxs/store/operators'
import { memoize } from 'lodash-es'
import { getPlaybackId, RealTimePlaybackKey } from '../../types'
import {
  ClearAllRealtimePlaybackPageItemSaveStatuses,
  ClearRealtimePlaybackPageItemSaveStatus,
  SetRealtimePlaybackPageItemSaveStatus,
  SetRealtimePlaybackPageLastEdited,
} from './realtime-playback-page-save-status.actions'
import {
  ErrorStatus,
  PlaybackPageItemSaveStatus,
  RealtimePlaybackPageSaveStatusInstanceModel,
  RealtimePlaybackPageSaveStatusStateModel,
} from './realtime-playback-page-save-status.model'

export function defaultRealtimePlaybackPageSaveStatusState(): RealtimePlaybackPageSaveStatusStateModel {
  return {
    playbackInstances: {},
  }
}

@State<RealtimePlaybackPageSaveStatusStateModel>({
  name: 'realtimePlaybackPageSaveStatusState',
  defaults: defaultRealtimePlaybackPageSaveStatusState(),
})
@Injectable()
export class RealtimePlaybackPageSaveStatusState {
  @Selector()
  private static allPlaybackInstanceStates(
    state: RealtimePlaybackPageSaveStatusStateModel,
  ): Record<string, RealtimePlaybackPageSaveStatusInstanceModel> {
    return state.playbackInstances
  }

  static readonly overallStatus = memoize(
    (
      playbackKey: RealTimePlaybackKey,
    ): ((
      playbackInstances: ReturnType<typeof RealtimePlaybackPageSaveStatusState.allPlaybackInstanceStates>,
    ) => PlaybackPageItemSaveStatus['type'] | undefined) => {
      return createSelector(
        [RealtimePlaybackPageSaveStatusState.allPlaybackInstanceStates],
        (playbackInstances: ReturnType<typeof RealtimePlaybackPageSaveStatusState.allPlaybackInstanceStates>) => {
          const instanceState = playbackInstances[getPlaybackId(playbackKey)]
          if (!instanceState?.itemStatuses) {
            return undefined
          }
          const itemStatuses = Object.values(instanceState.itemStatuses)
          if (itemStatuses.length === 0) {
            return undefined
          }
          if (itemStatuses.some(x => x.type === 'error')) {
            return 'error'
          }
          if (itemStatuses.some(x => x.type === 'saving')) {
            return 'saving'
          }
          return 'saved'
        },
      )
    },
    playbackKey => getPlaybackId(playbackKey),
  )

  static readonly errors = memoize(
    (
      playbackKey: RealTimePlaybackKey,
    ): ((
      playbackInstances: ReturnType<typeof RealtimePlaybackPageSaveStatusState.allPlaybackInstanceStates>,
    ) => Record<string, ErrorStatus>) => {
      return createSelector(
        [RealtimePlaybackPageSaveStatusState.allPlaybackInstanceStates],
        (playbackInstances: ReturnType<typeof RealtimePlaybackPageSaveStatusState.allPlaybackInstanceStates>) => {
          const instanceState = playbackInstances[getPlaybackId(playbackKey)]
          if (!instanceState?.itemStatuses) {
            return {}
          }
          return Object.fromEntries(
            Object.entries(instanceState.itemStatuses).filter(
              (itemStatus): itemStatus is [string, ErrorStatus] => itemStatus[1].type === 'error',
            ),
          )
        },
      )
    },
    playbackKey => getPlaybackId(playbackKey),
  )

  static readonly lastEdited = memoize(
    (
      playbackKey: RealTimePlaybackKey,
    ): ((
      playbackInstances: ReturnType<typeof RealtimePlaybackPageSaveStatusState.allPlaybackInstanceStates>,
    ) => ZonedDateTime | undefined) => {
      return createSelector(
        [RealtimePlaybackPageSaveStatusState.allPlaybackInstanceStates],
        (playbackInstances: ReturnType<typeof RealtimePlaybackPageSaveStatusState.allPlaybackInstanceStates>) =>
          playbackInstances[getPlaybackId(playbackKey)]?.lastEdited,
      )
    },
    playbackKey => getPlaybackId(playbackKey),
  )

  @Action(SetRealtimePlaybackPageItemSaveStatus)
  async setRealtimePlaybackPageItemSaveStatus(
    { setState }: StateContext<RealtimePlaybackPageSaveStatusStateModel>,
    { playbackKey, itemId, status }: SetRealtimePlaybackPageItemSaveStatus,
  ): Promise<void> {
    patchPlaybackInstanceState(setState, playbackKey, {
      itemStatuses: patch({ [itemId]: status }),
    })
  }

  @Action(ClearRealtimePlaybackPageItemSaveStatus)
  async clearRealtimePlaybackPageItemSaveStatus(
    { setState, getState }: StateContext<RealtimePlaybackPageSaveStatusStateModel>,
    { playbackKey, itemId }: ClearRealtimePlaybackPageItemSaveStatus,
  ): Promise<void> {
    const existingInstance = getState().playbackInstances[getPlaybackId(playbackKey)]
    if (!existingInstance) {
      return
    }
    patchPlaybackInstanceState(setState, playbackKey, {
      itemStatuses: Object.fromEntries(Object.entries(existingInstance.itemStatuses).filter(([xId]) => xId !== itemId)),
    })
  }

  @Action(ClearAllRealtimePlaybackPageItemSaveStatuses)
  async clearAllRealtimePlaybackPageItemSaveStatuses(
    { setState, getState }: StateContext<RealtimePlaybackPageSaveStatusStateModel>,
    { playbackKey }: ClearAllRealtimePlaybackPageItemSaveStatuses,
  ): Promise<void> {
    const instanceId = getPlaybackId(playbackKey)
    setState(
      patch<RealtimePlaybackPageSaveStatusStateModel>({
        playbackInstances: Object.fromEntries(
          Object.entries(getState().playbackInstances).filter(([xId]) => xId !== instanceId),
        ),
      }),
    )
  }

  @Action(SetRealtimePlaybackPageLastEdited)
  async setRealtimePlaybackPageLastEdited(
    { setState }: StateContext<RealtimePlaybackPageSaveStatusStateModel>,
    { playbackKey, lastEdited }: SetRealtimePlaybackPageLastEdited,
  ): Promise<void> {
    patchPlaybackInstanceState(setState, playbackKey, {
      lastEdited,
    })
  }
}

function patchPlaybackInstanceState(
  setState: StateContext<RealtimePlaybackPageSaveStatusStateModel>['setState'],
  playbackKey: RealTimePlaybackKey,
  update: ɵPatchSpec<RealtimePlaybackPageSaveStatusInstanceModel>,
): void {
  setState(
    patch<RealtimePlaybackPageSaveStatusStateModel>({
      playbackInstances: patch({
        [getPlaybackId(playbackKey)]: patch(update),
      }),
    }),
  )
}
